import type {DoppeActionType} from '../../client-server-common/doppe-action-types/doppe-action-type';
import {
    doppeActionTypeCreateSettingsResolvingFunction,
    doppeActionTypeDefineMetadata
} from '../../client-server-common/doppe-action-types/doppe-action-type';
import type {HideableValue} from '@wix/devzai-utils-common';
import {arrayRemoveNullishValues, arrayRemoveNullValues, hideableValueCreateHidden, immerProduce} from '@wix/devzai-utils-common';
import type {RichTextContent} from '@wix/devzai-utils-common';
import type {WixMediaResource} from '@wix/devzai-common-wix';
import {wixMediaResourceReplaceExternalImage} from '@wix/devzai-common-wix';
import type {BymoPagePropsContentSettingsMetadata} from '../../client-server-common';
import {
    BymoPagePropsContentSettingSemanticMeaning,
    BymoPagePropsContentSettingType,
    bymoPagePropsCreateContentSettingMetadata,
    DoppeBusinessEntity,
    DoppeExternalFormProvider,
    doppeExternalFormProviderResolveBusinessEntityReference,
} from '../../client-server-common';
import {DoppeSupportedWixTpas} from '../../client-server-common/types/doppe-supported-wix-tpas';
import {DoppeDtoUserMetaSiteType} from '../../client-server-common/types/doppe-dto-user';
import type {DoppeActionCtaButtonsSettings} from '../../client-server-common/types/doppe-action-cta-buttons-settings';
import {
    doppeActionCtaButtonsSettingsDefaultValuesMigration
} from '../../client-server-common/types/doppe-action-cta-buttons-settings';
import type {
    DoppeActionContentSectionsSettings
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import {
    doppeActionContentSectionSettingsDefaultValuesMigration
} from '../../client-server-common/types/doppe-action-content-sections-settings';

export namespace DoppeActionTypeExternalForm {

    export interface ActionSettings extends DoppeActionCtaButtonsSettings, DoppeActionContentSectionsSettings {
        formProvider: DoppeExternalFormProvider | null;
        formProviderId: string | null;

        images: WixMediaResource[] | null;
        title: HideableValue<string>;
        description: HideableValue<RichTextContent>;
    }
}

export const doppeActionTypeExternalFormActionContentSettingsMetadata = {
    images: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.MultipleMedia, BymoPagePropsContentSettingSemanticMeaning.EntityMedia),
    title: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityTitle),
    description: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.RichText, BymoPagePropsContentSettingSemanticMeaning.EntityDescription),
} satisfies BymoPagePropsContentSettingsMetadata<DoppeActionTypeExternalForm.ActionSettings>

export const doppeActionTypeExternalFormSettingsMetadata = {
    contentSettingsMetadata: doppeActionTypeExternalFormActionContentSettingsMetadata,
    supportsActionCtaButtons: {
        customCtaButtonTypes: {}
    },
    supportsContentSections: {
        customContentSectionsTypes: {}
    },
    hideableContentSettingsNames: {
        description: true,
        title: true
    },
    bindableContentSettingsNames: {}
} satisfies DoppeActionType.SettingsMetadata<DoppeActionTypeExternalForm.ActionSettings>

export const doppeActionTypeExternalForm = doppeActionTypeDefineMetadata<DoppeActionTypeExternalForm.ActionSettings>({
    id: 'co.hopp.action/wix-form',
    resolveActionSettings: doppeActionTypeCreateSettingsResolvingFunction<DoppeActionTypeExternalForm.ActionSettings>({
        formProvider: null,
        formProviderId: null,
        images: null,
        title: hideableValueCreateHidden(''),
        description: hideableValueCreateHidden(''),
        ...doppeActionContentSectionSettingsDefaultValuesMigration({}),
        ...doppeActionCtaButtonsSettingsDefaultValuesMigration({}, false),
    }),
    actionSettingsMetadata: doppeActionTypeExternalFormSettingsMetadata,
    actionListItemSettingsMetadata: false,
    isWidgetOpener: true,
    isFormAction: true,
    skipReportingOnGoalAchievedOnActionButtonClick: true,
    canRenderInPage: true,
    hasDisplaySettings: true,
    notSupportsSearch: true,
    lockIsNotSupported: true,
    resolveUsedBusinessEntities: actionSettings => {
        return arrayRemoveNullValues([
            actionSettings.formProvider !== null && actionSettings.formProviderId !== null ?
                doppeExternalFormProviderResolveBusinessEntityReference(
                    actionSettings.formProvider,
                    actionSettings.formProviderId
                ) :
                null
        ]);
    },
    resolveActionEntityBinding: actionSettings => {
        const {formProvider, formProviderId} = actionSettings;
        return formProvider !== null && formProviderId !== null
            ? doppeExternalFormProviderResolveBusinessEntityReference(formProvider, formProviderId)
            : null;
    },
    getSupportedBoundEntities: ({creatorsMetaSiteInstalledTpas}) =>
        arrayRemoveNullishValues([
            creatorsMetaSiteInstalledTpas.includes(DoppeSupportedWixTpas.WixForms) ? DoppeBusinessEntity.HoppForm : undefined,
        ]),
    getSupportedBoundEntitiesByTpa: () => ({
        [DoppeDtoUserMetaSiteType.CreatorsMetaSite]: {
            [DoppeSupportedWixTpas.WixForms]: [DoppeBusinessEntity.HoppForm],
        },
    }),
    resolveUsedMedia: actionSettings => {
        return actionSettings.images ?? [];
    },
    resolveMainMedia: actionSettings => {
        return actionSettings.images ? actionSettings.images[0] ?? null : null;
    },
    replaceExternalImages: (actionSettings, externalImagesUrlToImageResourceMap) => {
        return immerProduce(actionSettings, actionSettings => {
            if (actionSettings.images) {
                actionSettings.images = actionSettings.images.map(
                    image => wixMediaResourceReplaceExternalImage(image, externalImagesUrlToImageResourceMap))
            }
        })
    },
});
